<template>
    <q-dialog v-model="visible" persistent backdrop-filter="blur(4px) grayscale(50%)">
        <q-card style="max-width: 540px; min-width: 380px;">
            <q-card-section class="bg-primary">
                <div class="row items-center q-gutter-sm">
                    <q-icon name="account_balance" size="24px" color="white" />
                    <div class="text-h6" style="color: #ffffff;">{{ dialogTitle }}</div>
                </div>
            </q-card-section>

            <q-card-section>
                <!-- Lead question (offer-like modes only). KISS framing: this
                     is an OPT-IN offer, not an error to recover from. The
                     "(Subject To Availability)" caveat reflects Stripe's
                     risk-based eligibility gate: even with IBP enabled on the
                     package, the bank-pay option is only offered to the payer
                     when Stripe's risk system approves the specific transaction
                     (US bank, eligible merchant, amount under cap, etc.). When
                     unavailable, the payer simply sees card-only at signing. -->
                <div v-if="isOfferLike" class="text-body2 q-mb-md">
                    Do you want to enable Instant Bank Payments as a payment option for this package?
                    <span class="text-grey-7" style="font-style: italic;">(Subject To Availability)</span>
                </div>

                <!-- Lead for reconcile modes (IBP already on, something is now off). -->
                <div v-else class="text-body2 q-mb-md">
                    Instant Bank Payments is on for this package, but the setup needs a quick fix.
                </div>

                <!-- Benefits / requirements (offer-like modes only). The
                     "must sign last" requirement is hidden when there's only
                     one signer total, since "last" is meaningless then. -->
                <div v-if="isOfferLike">
                    <div class="text-subtitle2 q-mb-xs">What it gives you</div>
                    <div class="text-body2 q-mb-md">
                        Your payer can pay directly from their bank account, in addition to using a card or digital wallet.
                    </div>

                    <div class="text-subtitle2 q-mb-xs">What's required</div>
                    <ul class="q-mb-md q-pl-md" style="font-size: 13px;">
                        <li>Only one signer can collect payment.</li>
                        <li v-if="signerCount > 1">That signer must be the last one to sign.</li>
                    </ul>
                </div>

                <!-- Conflict / reconcile-conflict: 2+ payers. Same warning,
                     different action buttons below. -->
                <div v-if="mode === 'conflict' || mode === 'reconcile-conflict'"
                     class="q-pa-sm bg-orange-1 q-mb-none" style="border-radius: 6px;">
                    <div class="text-subtitle2 q-mb-xs">
                        <q-icon name="warning" size="18px" class="q-mr-xs" style="vertical-align: -3px; color: #f57c00;" />
                        You have {{ payerNames.length }} signers set to collect payment
                    </div>
                    <div class="text-body2 q-mb-xs">
                        <strong>{{ payerNames.join(', ') }}</strong>
                    </div>
                    <div class="text-body2">
                        To use Instant Bank Payments, edit the signers and keep only one as the payer.
                    </div>
                </div>

                <!-- Fixup / reconcile-fixup: planned auto-adjustments preview. -->
                <div v-else-if="mode === 'fixup' || mode === 'reconcile-fixup'"
                     class="q-pa-sm bg-amber-1 q-mb-none" style="border-radius: 6px;">
                    <div class="text-subtitle2 q-mb-xs">
                        <template v-if="mode === 'reconcile-fixup'">To keep this on, we will:</template>
                        <template v-else>When you enable, we will:</template>
                    </div>
                    <ul class="q-pl-md q-mb-none" style="font-size: 13px;">
                        <li v-for="(change, idx) in plannedChanges" :key="idx">{{ change }}</li>
                    </ul>
                </div>

                <!-- Reconcile-empty: IBP currently on, no payer set. -->
                <div v-else-if="mode === 'reconcile-empty'"
                     class="q-pa-sm bg-orange-1 q-mb-none" style="border-radius: 6px;">
                    <div class="text-subtitle2 q-mb-xs">
                        <q-icon name="warning" size="18px" class="q-mr-xs" style="vertical-align: -3px; color: #f57c00;" />
                        No signer is set to collect payment
                    </div>
                    <div class="text-body2">
                        Instant Bank Payments needs one signer to collect payment. Either turn it off and send card-only, or edit the signers to add a payer.
                    </div>
                </div>
            </q-card-section>

            <q-card-actions align="right">
                <!-- Conflict (IBP currently OFF, 2+ payers): one-time card-only
                     is correct semantics — nothing to persist disable for. -->
                <template v-if="mode === 'conflict'">
                    <q-btn flat label="Send with card only" color="grey-8" @click="resolve('decline')" />
                    <q-btn label="Edit signers" color="primary" unelevated @click="resolve('edit')" />
                </template>
                <!-- Reconcile-conflict (IBP currently ON, 2+ payers): MUST
                     persist disable=false, otherwise IBP flag stays on and
                     this same dialog re-fires on every subsequent send.
                     Plan §2.1 fix. -->
                <template v-else-if="mode === 'reconcile-conflict'">
                    <q-btn flat label="Edit signers" color="grey-8" @click="resolve('edit')" />
                    <q-btn label="Turn off bank payments and send" color="primary" unelevated @click="resolve('disable')" />
                </template>
                <!-- Reconcile-empty: IBP on, no payer. -->
                <template v-else-if="mode === 'reconcile-empty'">
                    <q-btn flat label="Edit signers" color="grey-8" @click="resolve('edit')" />
                    <q-btn label="Turn off bank payments and send" color="primary" unelevated @click="resolve('disable')" />
                </template>
                <!-- Reconcile-fixup: IBP on, fix invariants OR disable. -->
                <template v-else-if="mode === 'reconcile-fixup'">
                    <q-btn flat label="Turn off bank payments" color="grey-8" @click="resolve('disable')" />
                    <q-btn label="Fix and keep enabled" color="primary" unelevated @click="resolve('accept')" />
                </template>
                <!-- Fixup: IBP off, fix + enable OR decline. -->
                <template v-else-if="mode === 'fixup'">
                    <q-btn flat label="No, card only" color="grey-8" @click="resolve('decline')" />
                    <q-btn label="Yes, fix and enable" color="primary" unelevated @click="resolve('accept')" />
                </template>
                <!-- Clean offer: IBP off, invariants satisfied. -->
                <template v-else>
                    <q-btn flat label="No, card only" color="grey-8" @click="resolve('decline')" />
                    <q-btn label="Yes, enable" color="primary" unelevated @click="resolve('accept')" />
                </template>
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script>
/**
 * Reusable Instant Bank Payments offer / reconcile dialog.
 *
 * Single source of truth for the dialog markup, modes, and outcome semantics
 * used by every send affordance in the portal (PackagesBuilder, PackageDetails,
 * PackagesIndex). Hosts use it via a ref-based imperative open() API to keep
 * the call sites tiny:
 *
 *   <IbtOfferDialog ref="ibtDialog" />
 *   const outcome = await this.$refs.ibtDialog.open({ mode, plannedChanges, payerNames });
 *
 * Outcomes:
 *   'accept'  — sender enabled IBP (offer mode) OR re-fixed broken invariants
 *               (reconcile-fixup mode). Caller must persist the flip + run send.
 *   'disable' — sender opted to disable IBP on a broken template-derived
 *               package and send card-only. Caller persists the flip.
 *   'decline' — sender said "card only" on a fresh offer. No state change; send proceeds.
 *   'edit'    — sender chose to fix signers. Caller aborts send + navigates to
 *               the signers step (PackagesBuilder) or signers tab (PackageDetails).
 */
export default {
    name: 'IbtOfferDialog',

    data() {
        return {
            visible: false,
            mode: 'offer',
            plannedChanges: [],
            payerNames: [],
            signerCount: 0
            // Plan §7.1 — _resolver is intentionally NOT here. Vue 3 wraps
            // data() in reactive(); storing a promise resolver inside it
            // triggers Proxy traps on every access. The resolver is a
            // single-use callback, not reactive state. Assigned in created().
        };
    },

    created() {
        // Non-reactive instance field; see data() comment.
        this._resolver = null;
    },

    computed: {
        // Offer-like modes show the "Do you want to enable" framing plus the
        // What-it-gives-you / What's-required sections. Reconcile-* modes
        // suppress those (IBP is already on; the user just needs to fix the
        // configuration that drifted) so the body stays focused on the fix.
        isOfferLike() {
            return this.mode === 'offer'
                || this.mode === 'fixup'
                || this.mode === 'conflict';
        },

        // Titles match the framing of the body: offer-like modes are a
        // question ("Use Instant Bank Payments?"); reconcile-* modes are an
        // alert ("needs attention" / "needs one payer").
        dialogTitle() {
            switch (this.mode) {
                case 'reconcile-empty':
                case 'reconcile-fixup':
                    return 'Instant Bank Payments needs attention';
                case 'reconcile-conflict':
                    return 'Instant Bank Payments needs one payer';
                default:
                    return 'Use Instant Bank Payments for this package?';
            }
        }
    },

    methods: {
        /**
         * Imperative open. Returns a promise that resolves with the sender's
         * outcome string. Caller is responsible for handling each outcome.
         *
         * Plan §7.2 — defensive: if a prior open() is still pending (e.g.
         * caller double-clicked Send), resolve it as 'decline' before
         * accepting the new open. Otherwise the first promise would hang
         * forever once we overwrote _resolver.
         */
        open({ mode, plannedChanges = [], payerNames = [], signerCount = 0 }) {
            if (this._resolver) {
                const prior = this._resolver;
                this._resolver = null;
                prior('decline');
            }
            this.mode = mode;
            this.plannedChanges = plannedChanges;
            this.payerNames = payerNames;
            this.signerCount = signerCount;
            this.visible = true;
            return new Promise(resolve => {
                this._resolver = resolve;
            });
        },

        resolve(outcome) {
            const r = this._resolver;
            this._resolver = null;
            this.visible = false;
            // Clear next tick so reopening with a different mode renders cleanly.
            this.plannedChanges = [];
            this.payerNames = [];
            this.signerCount = 0;
            if (r) r(outcome);
        }
    }
};
</script>
